import type { IapProduct } from '../../hooks/iapProducts';

export type SkuSortByType = 'sku' | 'store' | 'active' | 'storefrontStatus';

export function filterIapProductsBySearch(
    products: IapProduct[],
    searchString: string,
): IapProduct[] {
    if (!searchString) {
        return products;
    }

    const lowered = searchString.toLowerCase();
    return products.filter(
        (product) =>
            product.sku?.toLowerCase().includes(lowered) ||
            product.packId?.toLowerCase().includes(lowered),
    );
}

export function filterIapProductsByStore(
    products: IapProduct[],
    store: string,
): IapProduct[] {
    if (store === 'all') {
        return products;
    }
    return products.filter((product) => product.store === store);
}

export function filterIapProductsByActive(
    products: IapProduct[],
    active: string,
): IapProduct[] {
    if (active === 'all') {
        return products;
    }
    const wantActive = active === 'active';
    return products.filter((product) => product.active === wantActive);
}

export function filterIapProductsByStorefrontStatus(
    products: IapProduct[],
    storefrontStatus: string,
): IapProduct[] {
    if (storefrontStatus === 'all') {
        return products;
    }
    if (storefrontStatus === 'NOT_SYNCED') {
        return products.filter(
            (product) =>
                product.storefrontStatus === 'NOT_SYNCED' ||
                product.storefrontStatus == null,
        );
    }
    return products.filter(
        (product) => product.storefrontStatus === storefrontStatus,
    );
}

function compareNullable(a?: string | null, b?: string | null): number {
    return (a ?? '').localeCompare(b ?? '');
}

const comparators: Record<
    SkuSortByType,
    (a: IapProduct, b: IapProduct) => number
> = {
    sku: (a, b) => a.sku.localeCompare(b.sku),
    store: (a, b) => a.store.localeCompare(b.store),
    active: (a, b) => Number(a.active) - Number(b.active),
    storefrontStatus: (a, b) =>
        compareNullable(a.storefrontStatus, b.storefrontStatus),
};

export function sortIapProducts(
    products: IapProduct[],
    sortBy: SkuSortByType | undefined,
    isSortAscend: boolean,
): IapProduct[] {
    if (!sortBy) {
        return products;
    }

    const sorted = products.slice().sort(comparators[sortBy]);
    return isSortAscend ? sorted : sorted.toReversed();
}

const PAGE_SIZE = 50;

export function getTotalPageCount(
    totalItems: number,
    pageSize: number = PAGE_SIZE,
): number {
    return Math.max(Math.ceil(totalItems / pageSize), 1);
}

export function paginate<T>(
    items: T[],
    page: number,
    pageSize: number = PAGE_SIZE,
): T[] {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
}
